<script lang="ts">
  import { FormInput, FormSelect, Button, MultiSelect } from '../common';
  import type { FilterValues } from '../../types/common';

  let {
    filters = $bindable({} as FilterValues),
    categories = [],
    institutions = [],
    people = [],
    onChange = () => {}
  }: {
    filters?: FilterValues;
    categories: string[];
    institutions: string[];
    people: string[];
    onChange?: () => void;
  } = $props();

  function clearAllFilters() {
    filters.search = '';
    filters.person = 'all';
    filters.institution = 'all';
    filters.startDate = '';
    filters.endDate = '';
    filters.minAmount = '';
    filters.maxAmount = '';
    filters.category = [];
    filters.showExcluded = false;
    onChange();
  }

  function filterUncategorized() {
    filters.category = ['Uncategorized'];
    // Clear amount filters to avoid conflicts
    filters.minAmount = '';
    filters.maxAmount = '';
    onChange();
  }

  function filterExpenses() {
    // Clear min, set max to negative values only
    filters.minAmount = '';
    filters.maxAmount = '-0.01';
    // Clear category filter to avoid conflicts
    filters.category = [];
    onChange();
  }

  function filterIncome() {
    // Set min to positive values only, clear max
    filters.minAmount = '0.01';
    filters.maxAmount = '';
    // Clear category filter to avoid conflicts
    filters.category = [];
    onChange();
  }
</script>

<div class="filter-controls">
  <!-- Quick Filter Buttons -->
  <div class="quick-filters">
    <button
      class="quick-filter-btn"
      onclick={filterUncategorized}
      type="button"
      title="Show only uncategorized transactions"
    >
      Uncategorized
    </button>
    <button
      class="quick-filter-btn"
      onclick={filterExpenses}
      type="button"
      title="Show only expenses (negative amounts)"
    >
      Expenses Only
    </button>
    <button
      class="quick-filter-btn"
      onclick={filterIncome}
      type="button"
      title="Show only income (positive amounts)"
    >
      Income Only
    </button>
  </div>

  <div class="filter-row">
    <div class="filter-group">
      <FormInput
        label="Search"
        bind:value={filters.search}
        placeholder="Search description..."
        oninput={onChange}
      />
    </div>

    <div class="filter-group">
      <MultiSelect
        bind:value={filters.category}
        options={categories}
        label="Category"
        placeholder="Select categories..."
        showSelectAll={true}
        maxHeight="400px"
        onchange={onChange}
      />
    </div>

    <div class="filter-group">
      <label for="person">Person</label>
      <select id="person" bind:value={filters.person} onchange={onChange}>
        <option value="all">All People</option>
        {#each people as person}
          <option value={person}>{person}</option>
        {/each}
      </select>
    </div>

    <div class="filter-group">
      <label for="institution">Institution</label>
      <select id="institution" bind:value={filters.institution} onchange={onChange}>
        <option value="all">All Institutions</option>
        {#each institutions as inst}
          <option value={inst}>{inst}</option>
        {/each}
      </select>
    </div>

    <div class="filter-group">
      <FormInput
        label="Start Date"
        type="date"
        bind:value={filters.startDate}
        onchange={onChange}
      />
    </div>

    <div class="filter-group">
      <FormInput
        label="End Date"
        type="date"
        bind:value={filters.endDate}
        onchange={onChange}
      />
    </div>

    <div class="filter-group">
      <FormInput
        label="Min Amount"
        type="number"
        bind:value={filters.minAmount}
        placeholder="-1000.00"
        oninput={onChange}
      />
    </div>

    <div class="filter-group">
      <FormInput
        label="Max Amount"
        type="number"
        bind:value={filters.maxAmount}
        placeholder="1000.00"
        oninput={onChange}
      />
    </div>
  </div>

  <div class="bottom-row">
    <label class="show-excluded-toggle">
      <input type="checkbox" bind:checked={filters.showExcluded} onchange={onChange} />
      <span>Show excluded transactions</span>
    </label>
    <Button variant="ghost" size="sm" onclick={clearAllFilters}>
      Clear All
    </Button>
  </div>
</div>

<style lang="scss">
  .filter-controls {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .quick-filters {
    display: flex;
    gap: var(--spacing-sm);
    flex-wrap: wrap;
  }

  .quick-filter-btn {
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--color-background-secondary);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    transition: all var(--transition-base) ease;
    white-space: nowrap;

    &:hover {
      background: var(--color-primary);
      color: white;
      border-color: var(--color-primary);
      transform: translateY(-1px);
      box-shadow: var(--shadow-sm);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .filter-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: var(--spacing-md);
    align-items: end;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);

    label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-secondary);
    }

    select {
      padding: var(--spacing-sm) var(--spacing-md);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      font-size: var(--font-size-sm);
      background: var(--color-background);
      transition: all var(--transition-fast);
      cursor: pointer;

      &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-focus-ring);
      }

      &:hover {
        border-color: var(--color-text-muted);
      }
    }
  }

  .bottom-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--spacing-sm);
  }

  .show-excluded-toggle {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    cursor: pointer;
    user-select: none;

    input[type="checkbox"] {
      cursor: pointer;
      margin: 0;
    }

    span {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
    }
  }

  @media (max-width: 1240px) {
    .filter-row {
      grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
      gap: var(--spacing-sm);
    }
  }

  @media (max-width: 1024px) {
    .filter-row {
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    }
  }

  @media (max-width: 768px) {
    .filter-row {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 640px) {
    .filter-row {
      grid-template-columns: 1fr;
    }

    .quick-filters {
      flex-direction: column;
    }

    .quick-filter-btn {
      width: 100%;
      text-align: center;
    }
  }
</style>
